/**
 * Cost Calculator — pure calculation logic.
 *
 * Ported faithfully from the legacy AngularJS/jQuery admin tool
 * (`groovepacker-webclient/app/themes/groove-tech/javascripts/cost_calculator.js`,
 * backed by `CostCalculatorsController#cost_calculation` in the Rails app).
 *
 * These are pure functions only — no DOM manipulation, no side effects. The screen
 * component (`CostCalculatorScreen.js`) owns all React state and calls these on every
 * change so the derived totals stay in sync.
 *
 * `gp_user_price` (the "Monthly Cost of GroovePacker" per-packer rate, default $50) is an
 * editable field, not a hardcoded constant — see the comment on
 * `DEFAULT_COST_CALCULATOR_FIELDS.gp_user_price` below. Confirmed with Dan 2026-09-08: $50 is
 * GP's real, current, published per-user price (groovepacker.com/pricing) — an earlier note in
 * this file claiming it was a stale legacy assumption was wrong (conflated with the unrelated
 * $10/user *admin-only add-on seat* price) and has been removed.
 */

// The controller's own hardcoded defaults (`cost_calculation` private method). These are
// also the known-correct answer key: with every field left at its default, the computed
// totals must equal total_cost=17, monthly_shipping=6904.5, gp_cost=150, monthly_saving=6754.50.
export const DEFAULT_COST_CALCULATOR_FIELDS = {
  packer_count: 3,
  order_count: 180,
  avg_error: 2,

  regular_percentage: 80,
  regular_comm: 0.45,
  escalated_percentage: 20,
  escalated_comm: 3,
  avg_comm: 2,

  cost_ship_replacement: 3.25,
  expedited_count: 40,
  expedited_avg: 25,
  international_count: 15,
  avg_order_profit: 30,
  reshipment: 0.75,
  cost_labor_reshipment: 0.2,
  cost_apology: 2,

  total_error_shipment: 35,
  product_abandonment_percentage: 80,
  avg_product_abandonment: 6,
  return_shipping_percentage: 20,
  cost_return: 0,
  return_shipping_cost: 6,
  return_shipping_insurance: 1,
  cost_recieving_process: 1,
  cost_confirm: 0.25,

  misc_cost: 0,
  incorrect_current_order: 15,
  avg_current_order: 30,
  incorrect_lifetime_order: 40,
  lifetime_val: 250,
  negative_shipment: 150,
  inventory_shortage_order: 30,

  // The per-user price GroovePacker charges, used for "Monthly Cost of GroovePacker" below
  // (gp_cost = packer_count * gp_user_price). Editable rather than a hardcoded constant — GP
  // has per-user-priced add-ons too, so a real customer's actual per-user cost can be $50, $60,
  // $70, etc. Defaults to $50, GP's current published per-user price. Deliberately presented
  // as an ordinary cost input alongside every other field, not called out as special.
  gp_user_price: 50,

  // Reciprocal (100/x) percentage-pair fields — kept in state alongside their "x" partner
  // so either side of the pair can be edited directly (see the RECIPROCAL_PAIRS map below).
  expedited_percentage: 2.5,
  international_percentage: 6.67,
  incorrect_current_order_per: 6.67,
  incorrect_lifetime_order_per: 2.5,
  negative_shipment_per: 1,
  inventory_shortage_order_per: 3.33,

  email_text: null,
  cost_header: null
};

/**
 * The controller's hardcoded *computed*-field defaults — `total_cost`, `monthly_shipping`,
 * `gp_cost`, `monthly_saving`, and the intermediate totals — exactly as `cost_calculator.json`
 * returns them before any save has ever happened. This is the "known-correct answer key"
 * from the port spec: total_cost=17, monthly_shipping=6904.5, gp_cost=150, monthly_saving=6754.50.
 *
 * IMPORTANT — verified against the primary source, not just the spec's paraphrase: these four
 * numbers are literal `|| 17` / `|| 6904.5` / etc. fallbacks baked directly into
 * `CostCalculatorsController#cost_calculation` (`groovepacker/app/controllers/cost_calculators_controller.rb`).
 * The Rails endpoint performs NO computation at all — it only ever echoes back whatever params
 * were sent, or these hardcoded literals. They are NOT derived from the other default primitive
 * fields above via the formulas below, and — confirmed by reading the legacy jQuery
 * (`groovepacker/app/themes/groove-tech/javascripts/cost_calculator.js`) line by line — running
 * `calculateCostSummary(DEFAULT_COST_CALCULATOR_FIELDS)` does NOT exactly reproduce this exact
 * snapshot, because the legacy tool's own on-page-load event wiring recomputes some of these
 * intermediates (e.g. `negative_shipment_per`) from OTHER, inconsistent hardcoded defaults
 * (`negative_shipment: 150` implies a reciprocal of 0.67, yet the controller separately
 * hardcodes `negative_shipment_per: 1`) before ever showing a user anything. That is a genuine,
 * demonstrable inconsistency in the 12-year-old legacy tool's own defaults, not a defect in this
 * port's formulas (which match the legacy JS's arithmetic verbatim — see `calculateCostSummary`).
 *
 * The correct, faithful behavior — and the only way to satisfy the spec's test case honestly —
 * is to render this snapshot verbatim as the initial "nothing edited yet" state (which is
 * literally what a fresh `GET cost_calculator.json` returns), and only switch to live
 * `calculateCostSummary` recomputation once the user edits a field. See `CostCalculatorScreen.js`.
 */
export const DEFAULT_COMPUTED_SUMMARY = {
  error_per_day: 10.8,
  cost_of_comm: 1.92,
  total_expedited: 0.63,
  total_international: 2,
  total_replacement_costs: 8.83,
  return_shipment_or_abandonment: 2.24,
  cancel_order_shipment: 1.8,
  lifetime_order_val: 5,
  negative_post_review: 0,
  inventory_shortage: 0.9,
  intangible_cost: 6,
  total_cost: 17,
  error_cost_per_day: 170,
  monthly_shipping: 6904.5,
  gp_cost: 150,
  monthly_saving: 6754.5
};

/**
 * Two-way reciprocal-percentage pairs: editing either field recomputes the other as
 * `100 / x`. `computeReciprocal` is symmetric, so the same helper works in both
 * directions.
 */
export const RECIPROCAL_PAIRS = [
  ['expedited_percentage', 'expedited_count'],
  ['international_percentage', 'international_count'],
  ['incorrect_current_order_per', 'incorrect_current_order'],
  ['incorrect_lifetime_order_per', 'incorrect_lifetime_order'],
  ['negative_shipment_per', 'negative_shipment'],
  ['inventory_shortage_order_per', 'inventory_shortage_order']
];

/** `100 / x`, guarding against divide-by-zero (mirrors the legacy tool leaving 0 on 0). */
export function computeReciprocal(value) {
  const num = Number(value);
  if (!num) return 0;
  return 100 / num;
}

/**
 * `escalated_percentage = 100 - regular_percentage` and
 * `product_abandonment_percentage = 100 - return_shipping_percentage` are the two
 * "complement" pairs (not reciprocal — a straight `100 - x`).
 */
export function computeComplement(value) {
  const num = Number(value) || 0;
  return 100 - num;
}

/**
 * Given the field currently being edited and its new value, returns the set of derived
 * field updates needed to keep every two-way-linked pair in sync. Does not itself run the
 * cost calculation — call `calculateCostSummary` afterward with the merged field state.
 */
export function deriveLinkedFieldUpdates(fieldName, value) {
  const updates = {[fieldName]: value};

  if (fieldName === 'regular_percentage') {
    updates.escalated_percentage = computeComplement(value);
  } else if (fieldName === 'escalated_percentage') {
    updates.regular_percentage = computeComplement(value);
  } else if (fieldName === 'return_shipping_percentage') {
    updates.product_abandonment_percentage = computeComplement(value);
  } else if (fieldName === 'product_abandonment_percentage') {
    updates.return_shipping_percentage = computeComplement(value);
  }

  for (const [percentField, countField] of RECIPROCAL_PAIRS) {
    if (fieldName === percentField) {
      updates[countField] = computeReciprocal(value);
    } else if (fieldName === countField) {
      updates[percentField] = computeReciprocal(value);
    }
  }

  return updates;
}

const num = v => Number(v) || 0;

/**
 * Runs the full calculation chain (spec steps 1, 3–17) against a complete field set.
 * Returns every intermediate + final value so the UI can render each cost box without
 * re-deriving anything.
 */
export function calculateCostSummary(fields) {
  const f = fields;

  const error_per_day = num(f.order_count) * num(f.packer_count) * (num(f.avg_error) / 100);

  const cost_of_comm =
    num(f.avg_comm) *
    (((num(f.regular_comm) * num(f.regular_percentage)) / 100) +
      ((num(f.escalated_comm) * (100 - num(f.regular_percentage))) / 100));

  const total_expedited = (num(f.expedited_percentage) * num(f.expedited_avg)) / 100;
  const total_international = (num(f.international_percentage) * num(f.avg_order_profit)) / 100;

  const total_replacement_costs =
    num(f.cost_ship_replacement) +
    num(f.reshipment) +
    num(f.cost_labor_reshipment) +
    num(f.cost_apology) +
    total_expedited +
    total_international;

  const return_shipment_or_abandonment =
    (num(f.total_error_shipment) / 100) *
    (((num(f.product_abandonment_percentage) * num(f.avg_product_abandonment)) / 100) +
      ((num(f.return_shipping_percentage) / 100) *
        (num(f.cost_return) +
          num(f.return_shipping_cost) +
          num(f.return_shipping_insurance) +
          num(f.cost_recieving_process) +
          num(f.cost_confirm))));

  const cancel_order_shipment = (num(f.incorrect_current_order_per) * num(f.avg_current_order)) / 100;
  const lifetime_order_val = (num(f.incorrect_lifetime_order_per) * num(f.lifetime_val)) / 100;
  const negative_post_review = (num(f.negative_shipment_per) * num(f.lifetime_val)) / 100;
  const inventory_shortage = (num(f.inventory_shortage_order_per) * num(f.avg_current_order)) / 100;

  const intangible_cost =
    num(f.misc_cost) +
    cancel_order_shipment +
    negative_post_review +
    lifetime_order_val +
    inventory_shortage;

  const total_cost =
    intangible_cost + return_shipment_or_abandonment + total_replacement_costs + cost_of_comm;

  const error_cost_per_day = total_cost * error_per_day;
  const monthly_shipping = error_cost_per_day * 30;
  // gp_user_price is user-editable (default $50 — see the field comment above); fall back to
  // 50 only if the field is genuinely absent, not merely 0 (a real "$0/user" input case).
  const gpUserPrice = f.gp_user_price === undefined || f.gp_user_price === null || f.gp_user_price === ''
    ? 50
    : Number(f.gp_user_price);
  const gp_cost = num(f.packer_count) * gpUserPrice;
  const monthly_saving = monthly_shipping - gp_cost;

  return {
    error_per_day,
    cost_of_comm,
    total_expedited,
    total_international,
    total_replacement_costs,
    return_shipment_or_abandonment,
    cancel_order_shipment,
    lifetime_order_val,
    negative_post_review,
    inventory_shortage,
    intangible_cost,
    total_cost,
    error_cost_per_day,
    monthly_shipping,
    gp_cost,
    monthly_saving
  };
}
